import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase';

interface VehiclePrice {
  id: string;
  name: string;
  brand: string;
  type: string;
  year: number;
  price: number;
  selling_price: number;
  updated_at: string;
  created_at: string;
}

const formatCurrency = (n: number) =>
  n.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' });

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString('vi-VN');

const formatNumberInput = (raw: string) => {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  return Number(digits).toLocaleString('vi-VN');
};

const parseNumberInput = (formatted: string) => {
  return Number(formatted.replace(/\./g, '').replace(/,/g, ''));
};

export default function VehiclePricesPage() {
  const [data, setData] = useState<VehiclePrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<VehiclePrice | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: '',
    brand: '',
    type: 'Xe máy',
    year: new Date().getFullYear(),
    price: '',
    selling_price: '',
  });

  const fetchData = async () => {
    setLoading(true);
    const { data: rows, error } = await supabase
      .from('vehicle_prices')
      .select('*')
      .order('updated_at', { ascending: false });

    setData((error ? [] : rows || []) as VehiclePrice[]);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        item.brand.toLowerCase().includes(q) ||
        item.type.toLowerCase().includes(q)
    );
  }, [data, search]);

  const openAdd = () => {
    setEditItem(null);
    setForm({
      name: '',
      brand: '',
      type: 'Xe máy',
      year: new Date().getFullYear(),
      price: '',
      selling_price: '',
    });
    setModalOpen(true);
  };

  const openEdit = (item: VehiclePrice) => {
    setEditItem(item);
    setForm({
      name: item.name,
      brand: item.brand,
      type: item.type,
      year: item.year,
      price: formatNumberInput(item.price.toString()),
      selling_price: formatNumberInput((item.selling_price ?? 0).toString()),
    });
    setModalOpen(true);
  };

  const handlePriceChange = (value: string) => {
    const formatted = formatNumberInput(value);
    setForm((prev) => ({ ...prev, price: formatted }));
  };

  const handleSellingPriceChange = (value: string) => {
    const formatted = formatNumberInput(value);
    setForm((prev) => ({ ...prev, selling_price: formatted }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.price.trim()) return;
    setSaving(true);

    const payload = {
      name: form.name.trim(),
      brand: form.brand.trim(),
      type: form.type,
      year: Number(form.year),
      price: parseNumberInput(form.price),
      selling_price: parseNumberInput(form.selling_price),
      updated_at: new Date().toISOString(),
    };

    if (editItem) {
      await supabase.from('vehicle_prices').update(payload).eq('id', editItem.id);
      const updated = data.map((d) =>
        d.id === editItem.id ? { ...d, ...payload } : d
      );
      setData(updated);
    } else {
      const { data: inserted } = await supabase
        .from('vehicle_prices')
        .insert(payload)
        .select()
        .single();
      if (inserted) {
        setData([inserted as VehiclePrice, ...data]);
      }
    }

    setSaving(false);
    setModalOpen(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await supabase.from('vehicle_prices').delete().eq('id', deleteId);
    setData(data.filter((d) => d.id !== deleteId));
    setDeleteId(null);
  };

  return (
    <div className="p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">Giá Xe</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Bảng giá tham khảo cầm xe máy — có ngày cập nhật
          </p>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#f59e0b] hover:bg-[#d97706] text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap self-start sm:self-auto"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line text-base"></i>
          </span>
          Thêm giá xe
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
          <i className="ri-search-line text-base"></i>
        </span>
        <input
          type="text"
          placeholder="Tìm theo tên xe, hãng, loại..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/30 focus:border-[#f59e0b] bg-white"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-10 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-gray-200 border-t-[#f59e0b] rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Tên xe</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Hãng</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Loại</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Năm SX</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Giá cầm</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Giá bán ra</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Cập nhật</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item) => (
                    <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">{item.name}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{item.brand}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-amber-50 text-amber-700 border border-amber-100">
                          {item.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{item.year}</td>
                      <td className="px-4 py-3 font-medium text-[#f59e0b] whitespace-nowrap">{formatCurrency(item.price)}</td>
                      <td className="px-4 py-3 font-medium text-emerald-600 whitespace-nowrap">{formatCurrency(item.selling_price ?? 0)}</td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">{formatDate(item.updated_at)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEdit(item)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-amber-50 text-amber-600 transition-colors cursor-pointer"
                            title="Sửa"
                          >
                            <i className="ri-edit-line text-base"></i>
                          </button>
                          <button
                            onClick={() => setDeleteId(item.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-500 transition-colors cursor-pointer"
                            title="Xóa"
                          >
                            <i className="ri-delete-bin-line text-base"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {filtered.map((item) => (
                <div key={item.id} className="p-4 space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-gray-800">{item.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{item.brand} · {item.year}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEdit(item)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-amber-50 text-amber-600 transition-colors cursor-pointer"
                      >
                        <i className="ri-edit-line text-base"></i>
                      </button>
                      <button
                        onClick={() => setDeleteId(item.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-500 transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-base"></i>
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    <span className="inline-flex px-2 py-0.5 rounded-md font-medium bg-amber-50 text-amber-700 border border-amber-100">
                      {item.type}
                    </span>
                    <span className="text-gray-400">|</span>
                    <span className="font-medium text-[#f59e0b]">{formatCurrency(item.price)}</span>
                    <span className="text-gray-400">|</span>
                    <span className="font-medium text-emerald-600">Bán: {formatCurrency(item.selling_price ?? 0)}</span>
                  </div>
                  <p className="text-xs text-gray-400">Cập nhật: {formatDate(item.updated_at)}</p>
                </div>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="p-10 text-center text-gray-400 text-sm">
                <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-gray-100 text-gray-300">
                  <i className="ri-search-line text-2xl"></i>
                </span>
                Không tìm thấy xe nào
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800">
                {editItem ? 'Sửa giá xe' : 'Thêm giá xe'}
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-400"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Tên xe</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/30 focus:border-[#f59e0b]"
                  placeholder="VD: Honda Wave Alpha"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Hãng xe</label>
                  <input
                    type="text"
                    value={form.brand}
                    onChange={(e) => setForm({ ...form, brand: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/30 focus:border-[#f59e0b]"
                    placeholder="VD: Honda"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Loại xe</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/30 focus:border-[#f59e0b] bg-white"
                  >
                    <option value="Xe máy">Xe máy</option>
                    <option value="Xe tay ga">Xe tay ga</option>
                    <option value="Xe côn tay">Xe côn tay</option>
                    <option value="Xe điện">Xe điện</option>
                    <option value="Xe khác">Xe khác</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Năm sản xuất</label>
                  <input
                    type="number"
                    value={form.year}
                    onChange={(e) => setForm({ ...form, year: Number(e.target.value) })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/30 focus:border-[#f59e0b]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Giá cầm (VNĐ)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={form.price}
                    onChange={(e) => handlePriceChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/30 focus:border-[#f59e0b]"
                    placeholder="VD: 8.000.000"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Giá bán ra (VNĐ)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={form.selling_price}
                    onChange={(e) => handleSellingPriceChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/30 focus:border-[#f59e0b]"
                    placeholder="VD: 10.000.000"
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Hủy
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim() || !form.price.trim()}
                className="px-4 py-2 text-sm font-medium bg-[#f59e0b] hover:bg-[#d97706] text-white rounded-lg transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {saving ? 'Đang lưu...' : 'Lưu'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-50 flex items-center justify-center text-red-500">
              <i className="ri-delete-bin-line text-xl"></i>
            </div>
            <h3 className="font-semibold text-gray-800 mb-1">Xóa giá xe?</h3>
            <p className="text-sm text-gray-500 mb-5">Thao tác này không thể hoàn tác.</p>
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => setDeleteId(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Hủy
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm font-medium bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Xóa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}